// Zebra Expr — interpreter. Walks an AST against an "item tree".
//
// item = { fields: { normalisedFieldName: value, ... }, children: [item, ...] }
// The root item is the row the formula computes for; children carry the tree
// so aggregations (#children / #subtree / #leaves) can traverse it. This model
// is deliberately decoupled from Jira issues — index.js adapts real issues into
// it, and tests build item trees directly.

import { parse } from './parser.js';
import { ExprError } from './lexer.js';
import { BUILTINS, LAZY, num, str, isTruthy, toDate, Duration, ucmp, patternRegex, percentile } from './builtins.js';

// Default aggregate scope. Matches Structure Expr, whose bare `SUM { … }`
// aggregates over the item + all its sub-items — so real Structure formulas
// copy-paste faithfully. Use `#children` to restrict to direct children.
const DEFAULT_SCOPE = 'subtree';

const normalize = (n) => String(n).toLowerCase().replace(/[^a-z0-9]/g, '');
// Canonical key for a WITH binding / user-function parameter. Upper-case, the
// same convention the parser already applies to call names and `withfn` names,
// so every kind of name in the language is matched case-insensitively.
const VARKEY = (n) => String(n).toUpperCase();

// Structure-name aliases → our canonical function names.
export const FN_ALIASES = {
  IF_ERROR: 'IFERR', IFERROR: 'IFERR', ISEMPTY: 'IS_EMPTY', ISNULL: 'IS_NULL',
  ISERROR: 'ISERR', ISBLANK: 'IS_EMPTY', ISDEFINED: 'DEFINED',
  IFS: 'IF', SWITCH: 'CASE', // multi-branch aliases (stay lazy via IF/CASE)
  CEIL: 'CEILING', TO_NUMBER: 'NUMBER', TONUMBER: 'NUMBER', TO_TEXT: 'TEXT', TOTEXT: 'TEXT', STRING: 'TEXT',
  ADD_DAYS: 'DATE_ADD', INCLUDES: 'CONTAINS', LENGTH: 'LEN', COUNT_IF: 'COUNT',
  AVG: 'AVERAGE', COLOUR: 'COLOR', BG: 'BACKGROUND',
};

function descendants(item) {
  const out = [];
  const walk = (it) => { for (const c of (it.children || [])) { out.push(c); walk(c); } };
  walk(item);
  return out;
}
function ancestors(item) {
  const out = [];
  for (let a = item.__parent; a; a = a.__parent) out.push(a);
  return out;
}
function scopeItems(item, type, extra) {
  switch (type) {
    case 'subtree': return [item, ...descendants(item)];
    // Structure's #strict: "do not process the current row item as part of the
    // aggregation" — every descendant, self excluded. This used to fall through
    // to the children-only default, so SUM#strict{…} on a three-level tree
    // silently dropped the grandchildren.
    case 'strict': return descendants(item);
    // #ancestors: the path from the ROOT down to this row, self LAST — the
    // order Structure's JOIN (whose default scope this is) joins in: "all
    // parent string values from root to the self value". #fromLevel/#toLevel
    // trim the path: a positive level is absolute (1 = root), zero or negative
    // is relative to this row (0 = self, -1 = parent).
    case 'ancestors': {
      const path = [...ancestors(item).reverse(), item];
      const L = path.length;
      const lvl = (v, dflt) => {
        if (v === undefined || v === true) return dflt;
        const n = parseInt(v, 10);
        if (Number.isNaN(n)) return dflt;
        return n > 0 ? n : L + n;
      };
      const from = Math.max(1, lvl(extra && extra.fromlevel, 1));
      const to = Math.min(L, lvl(extra && extra.tolevel, L));
      return from <= to ? path.slice(from - 1, to) : [];
    }
    // A node's leaves = the leaf nodes of its whole subtree, INCLUDING the node
    // itself when it has no children. Matches Structure Expr: a leaf row is its
    // own leaf, so `SUM#leaves{…}` renders a value on individual work items, not
    // just on parents. (Parents are unaffected — they have children, so they're
    // filtered out and only their leaf descendants remain.)
    case 'leaves': return [item, ...descendants(item)].filter((d) => !(d.children && d.children.length));
    case 'all': return descendants(item);
    case 'children':
    default: return item.children || [];
  }
}

// Per-(item, variable) memo. Keyed by the marker OBJECT, so re-binding a
// variable produces new markers and the old values are unreachable — no stale
// value can survive an edit. WeakMap on the item so a dropped subtree is
// collectable.
const VAR_MEMO = new WeakMap(); // item -> Map(marker -> value)
// Markers currently being evaluated. A variable that reads itself (directly or
// through another) resolves to blank rather than recursing until the stack dies.
const VAR_BUSY = new Set();

function resolveVar(marker, item, vars) {
  let byMarker = VAR_MEMO.get(item);
  if (byMarker && byMarker.has(marker)) return byMarker.get(marker);
  if (VAR_BUSY.has(marker)) return undefined;
  VAR_BUSY.add(marker);
  let value;
  try {
    value = evalNode(marker.ast, item, vars);
  } catch {
    value = undefined; // a binding that blows up reads as blank, not Error
  } finally {
    VAR_BUSY.delete(marker);
  }
  if (!byMarker) { byMarker = new Map(); VAR_MEMO.set(item, byMarker); }
  byMarker.set(marker, value);
  return value;
}

function resolveField(name, item, vars) {
  // WITH bindings live on a prototype scope-chain (each nested WITH is an
  // Object.create(parent)), so an inner WITH must still see OUTER WITH
  // variables — read through the chain, not just the innermost frame. The root
  // scope is null-prototype (interpret seeds vars = null), so no Object.prototype
  // keys ("toString" etc.) can leak in. Mirrors how evalCall resolves functions.
  //
  // Case-INSENSITIVE, like every other name in Structure Expr: function names
  // are upper-cased at parse time and field names go through normalize(), so a
  // case-sensitive variable lookup was the odd one out. Real the archive client formulas rely
  // on this — insight 64 "Reorder" binds `WITH OrderNOW = …` and then reads
  // `OrderNow`, which used to resolve to undefined and rendered
  // "REORDER:  Stock Items" with the count silently missing.
  if (vars) {
    const v = vars[VARKEY(name)];
    // An insight VARIABLE is a marker holding an AST, not a value — it has to be
    // evaluated against THIS item, because `sum { zarvalue }` reads a different
    // value on every descendant it visits. See expr/variables.js.
    if (v !== undefined && v !== null && v.__exprVar) return resolveVar(v, item, vars);
    if (v !== undefined) return v;
  }
  // ENGINE-SUPPLIED row properties. `level` is a Structure built-in — the row's
  // depth in the hierarchy, where a folder's top work items are Level 1 and
  // each group tier pushes its contents one level down (the same scale the
  // sorters use, see sortIssues in Structure.jsx). It is NOT a field, so it has
  // to be answered here; without it `level` resolved to blank, `level = 1` was
  // never true and `level != 1` was always true. 41 the archive client formula columns branch
  // on it — every one of them took the wrong branch, which is what made
  // "Target COS %" render the SUM of every project's target (315 %) instead of
  // the average (35 %).
  const lvlName = normalize(name);
  if (lvlName === 'level' && typeof item.level === 'number') return item.level;
  if (lvlName === 'depth' && typeof item.level === 'number') return item.level;

  const f = item.fields || {};
  if (Object.prototype.hasOwnProperty.call(f, name)) return f[name];
  const nn = normalize(name);
  if (Object.prototype.hasOwnProperty.call(f, nn)) return f[nn];
  return undefined;
}

function applyAggregate(name, values, scope) {
  const nums = values.map(num).filter((x) => !Number.isNaN(x));
  switch (name) {
    case 'SUM': return nums.reduce((p, c) => p + c, 0);
    case 'AVG': {
      // Blanks are EXCLUDED, not averaged as 0 — the same posture MIN/MAX take
      // below. num() deliberately maps blank to 0 for scalar context, but these
      // aggregates read `avg{if(cond, x)}` where every non-matching row yields
      // undefined; counting those as 0 in the denominator deflated every such
      // rollup (Sika South Africa's AVG ZAR read 2,302.1 against Jira's
      // 2,762.5 — exactly 2x off on a single-leaf bucket).
      const nonEmpty = values.filter((v) => v != null && v !== '');
      if (!nonEmpty.length) return null;
      // Date-aware for the same reason MIN/MAX are: num() parseFloats
      // "2026-08-07" to its YEAR, so averaging dates numerically returns ~2026
      // rather than a date, which flipped Procurement's INFO warning on 564 of
      // 674 rollups.
      const dateVals = nonEmpty.filter(dateish).map(toDate).filter(Boolean);
      if (dateVals.length === nonEmpty.length) {
        const ts = dateVals.map((d) => d.getTime());
        return new Date(ts.reduce((p, c) => p + c, 0) / ts.length);
      }
      const ns = nonEmpty.map(num).filter((x) => !Number.isNaN(x));
      return ns.length ? ns.reduce((p, c) => p + c, 0) / ns.length : null;
    }
    case 'MIN':
    case 'MAX': {
      // Date-aware: num() parseFloats a date string to its YEAR ("2026-08-07"
      // -> 2026), so a numeric MIN/MAX over dates returns a bare year and every
      // later comparison against a real date (today(), DATE_ADD(...)) breaks.
      // If EVERY non-empty value is a date, min/max by timestamp and return a
      // Date so the relational operators (order()) stay date-correct. Numbers
      // (dateish=false) keep the numeric path unchanged.
      // MIN/MAX ignore blanks entirely (Structure parity) — a blank must NOT
      // count as 0, otherwise MAX over an all-blank set returns 0 (a 1970 date
      // once compared) instead of null, and an empty result can't be caught by
      // IS_EMPTY. This is unlike SUM/AVG, where num() deliberately treats blank
      // as 0.
      const nonEmpty = values.filter((v) => v != null && v !== '');
      const dateVals = nonEmpty.filter(dateish).map(toDate).filter(Boolean);
      if (nonEmpty.length > 0 && dateVals.length === nonEmpty.length) {
        const ts = dateVals.map((d) => d.getTime());
        return new Date(name === 'MAX' ? Math.max(...ts) : Math.min(...ts));
      }
      const numVals = nonEmpty.map(num).filter((x) => !Number.isNaN(x));
      return numVals.length ? (name === 'MAX' ? Math.max(...numVals) : Math.min(...numVals)) : null;
    }
    // Structure: "a count of DEFINED values (or truthy values, if #truthy)".
    // Defined = not empty; a 0 counts, so `COUNT{storypoints}` tallies every
    // estimated row including the zeros. Our comparisons yield booleans where
    // Structure yields 1/0, and a false is deliberately NOT counted — so a
    // `COUNT{status = "Done"}` written without #truthy still counts the done
    // rows rather than every row.
    case 'COUNT': {
      if (scope.extra && scope.extra.truthy) return values.filter(isTruthy).length;
      return values.filter((v) => v != null && v !== '' && v !== false && !(typeof v === 'number' && Number.isNaN(v))).length;
    }
    case 'JOIN': {
      const sep = (scope.extra && scope.extra.separator != null) ? String(scope.extra.separator) : ', ';
      return orderValues(values.filter((v) => v != null && v !== '').map(str), scope).join(sep);
    }
    // Rank aggregates. Blanks excluded, same reasoning as AVG — a median that
    // counts every non-matching row as 0 collapses toward 0 as the subtree
    // grows. MEDIAN = PERCENTILE#p=0.5; QUARTILE1/3 = p 0.25 / 0.75; PERCENTILE
    // takes #p as a fraction (0.95) or a percentage (95), as Structure does.
    case 'MEDIAN':
    case 'QUARTILE1':
    case 'QUARTILE3':
    case 'PERCENTILE': {
      const ns = values.filter((v) => v != null && v !== '')
        .map(num).filter((x) => !Number.isNaN(x));
      if (!ns.length) return null;
      let p = name === 'MEDIAN' ? 0.5 : name === 'QUARTILE1' ? 0.25 : name === 'QUARTILE3' ? 0.75 : undefined;
      if (p === undefined) {
        const raw = scope.extra ? scope.extra.p : undefined;
        p = parseFloat(raw);
        if (Number.isNaN(p)) throw new ExprError('PERCENTILE needs #p, e.g. PERCENTILE#p=0.9{…}', 0);
      }
      return percentile(ns, p);
    }
    case 'VALUES': return [...new Set(values.filter((v) => v != null))];
    // #flatten expands one level of nested arrays (a multi-value field per
    // row); #compact drops the undefined entries those leave behind.
    case 'ARRAY': {
      let vals = values;
      if (scope.extra && scope.extra.flatten) vals = vals.flat(1);
      if (scope.extra && scope.extra.compact) vals = vals.filter((v) => v !== undefined);
      return orderValues(vals.filter((v) => v != null), scope);
    }
    default: throw new ExprError(`Unknown aggregate "${name}"`, 0);
  }
}

// The collection modifiers JOIN and ARRAY share (Structure): #distinct keeps
// the first of each repeated value, #reverse walks the rows backwards. Both
// were accepted by the lexer and then ignored, so JOIN#distinct{assignee}
// listed the same person three times.
function orderValues(list, scope) {
  const extra = (scope && scope.extra) || {};
  let out = list;
  if (extra.distinct) {
    const seen = new Set();
    out = out.filter((v) => { const k = typeof v === 'object' && v !== null ? JSON.stringify(v) : `${typeof v}:${v}`; if (seen.has(k)) return false; seen.add(k); return true; });
  }
  if (extra.reverse) out = [...out].reverse();
  return out;
}

// Order two operands for the relational operators. Prefer numeric, but if
// either side is a real date (a Date object, or a string that clearly parses as
// a date) compare by timestamp — otherwise `duedate < today()` coerces both
// sides through num() to NaN (or to a bare year) and never matches. This makes
// date comparisons behave like Structure Expr.
function dateish(v) {
  if (v instanceof Date) return true;
  return typeof v === 'string' && /\d{4}-\d\d|\d\d?\/\d\d?|T\d/.test(v) && toDate(v) != null;
}
// Both operands resolve to real dates — the case where a subtraction is an
// elapsed time rather than plain arithmetic.
function bothDates(l, r) {
  return (dateish(l) || dateish(r)) && toDate(l) != null && toDate(r) != null;
}
function order(l, r) {
  if (bothDates(l, r)) return [toDate(l).getTime(), toDate(r).getTime()];
  return [num(l), num(r)];
}

// Binary operators.
function binary(op, l, r) {
  switch (op) {
    case 'AND': return isTruthy(l) && isTruthy(r);
    case 'OR': return isTruthy(l) || isTruthy(r);
    // Infix CONCAT — same null-to-empty semantics as the CONCAT builtin.
    case 'CONCAT': return str(l) + str(r);
    case '+':
      if (typeof l === 'string' || typeof r === 'string') return str(l) + str(r);
      return num(l) + num(r);
    // Date parity: subtracting two dates yields a DURATION — Structure types the
    // result that way and renders it readably, so `NOW() - Updated` shows
    // "3w 2d 4h" rather than the raw millisecond delta. Duration stays numeric
    // for every downstream use (see builtins.Duration), so dividing it or
    // comparing it is unaffected. Non-date operands fall through order() to
    // plain numeric subtraction, byte-for-byte unchanged.
    case '-': {
      const [a, b] = order(l, r);
      return bothDates(l, r) ? new Duration(a - b) : a - b;
    }
    case '*': return num(l) * num(r);
    // Structure parity: division by zero is an ERROR (not NaN/Infinity), so an
    // IF_ERROR/IFERR wrapping the ratio catches it and applies the fallback —
    // e.g. a "% complete" over an empty set errors -> IF_ERROR(...,"0 %") -> "0 %",
    // and if_error(0/0,999) -> 999. Requires lazy WITH (below) so the error inside
    // a WITH binding reaches the IF_ERROR that USES the binding.
    case '/': { const b = num(r); if (b === 0) throw new ExprError('Division by zero', 0); return num(l) / b; }
    case '%': { const b = num(r); if (b === 0) throw new ExprError('Modulo by zero', 0); return num(l) % b; }
    case '=':
    case '!=': {
      let eq;
      if (typeof l === 'number' || typeof r === 'number') eq = num(l) === num(r);
      // Dates compare by instant, as the relational operators already do.
      // Without this `duedate = TODAY()` compared the field's text
      // ("2026-09-10") with the Date's text and was never true — Structure
      // treats both as timestamps, so "due today" formulas copied from it
      // silently never fired.
      else if (bothDates(l, r)) eq = toDate(l).getTime() === toDate(r).getTime();
      else eq = str(l).toLowerCase() === str(r).toLowerCase();
      return op === '=' ? eq : !eq;
    }
    case '<': { const [a, b] = order(l, r); return a < b; }
    case '<=': { const [a, b] = order(l, r); return a <= b; }
    case '>': { const [a, b] = order(l, r); return a > b; }
    case '>=': { const [a, b] = order(l, r); return a >= b; }
    default: throw new ExprError(`Unknown operator "${op}"`, 0);
  }
}

function evalNode(node, item, vars) {
  switch (node.kind) {
    case 'num': return node.value;
    case 'str': return node.value;
    case 'bool': return node.value;
    case 'null': return null;
    // A precomputed literal — carries any JS value verbatim. The parser never
    // emits it; it's how decompose.js substitutes a stored aggregate result back
    // into an AST so a rollup can be re-derived from components without a walk.
    case 'lit': return node.value;
    case 'field': return resolveField(node.name, item, vars);
    case 'prop': {
      const obj = evalNode(node.object, item, vars);
      if (obj == null) return undefined;
      if (typeof obj === 'object') return obj[node.name] ?? obj[normalize(node.name)];
      return undefined;
    }
    case 'unary':
      if (node.op === 'NOT') return !isTruthy(evalNode(node.operand, item, vars));
      return -num(evalNode(node.operand, item, vars));
    case 'binary': {
      // AND/OR are value-preserving (like JS &&/||) and short-circuit, so
      // `If(c1, r1) or If(c2, r2)` returns the first defined result — matching
      // Structure Expr's selection idiom.
      if (node.op === 'AND') { const l = evalNode(node.left, item, vars); return isTruthy(l) ? evalNode(node.right, item, vars) : l; }
      if (node.op === 'OR') { const l = evalNode(node.left, item, vars); return isTruthy(l) ? l : evalNode(node.right, item, vars); }
      return binary(node.op, evalNode(node.left, item, vars), evalNode(node.right, item, vars));
    }
    case 'if':
      if (isTruthy(evalNode(node.cond, item, vars))) return evalNode(node.then, item, vars);
      return node.else == null ? undefined : evalNode(node.else, item, vars); // ELSE optional

    case 'with': {
      // LAZY + memoized binding: the value is computed on first reference, not at
      // definition. This matters when the value can ERROR (e.g. a 0/0 ratio): the
      // error then surfaces WHERE the binding is used, so an IF_ERROR in the body
      // catches it — matching Structure. Unreferenced bindings never evaluate.
      const child = Object.create(vars || null);
      let cached, computed = false;
      Object.defineProperty(child, VARKEY(node.name), {
        enumerable: true, configurable: true,
        get() { if (!computed) { cached = evalNode(node.value, item, vars); computed = true; } return cached; },
      });
      return evalNode(node.body, item, child);
    }
    case 'withfn': {
      // Define a user function, then evaluate the body with it in scope.
      // Stored under the upper-cased name (call names are upper-cased), and the
      // closure includes the binding so the function can recurse.
      const child = Object.create(vars || null);
      const fn = { __fn: true, params: node.params, body: node.value };
      fn.closure = child;
      child[VARKEY(node.name)] = fn;
      return evalNode(node.body, item, child);
    }
    case 'template':
      return node.parts.map((p) => str(evalNode(p, item, vars))).join('');
    // An explicit lambda (`x -> x * 2`) is a function VALUE: same shape as a
    // WITH-defined function, closed over the scope it was written in.
    case 'lambda':
      return { __fn: true, params: node.params, body: node.body, closure: vars };
    case 'agg': {
      // PARENT{…} / PARENT#level=N{…} — evaluate the body on an ANCESTOR row
      // rather than aggregating a scope. Structure semantics:
      //   PARENT{expr}            immediate parent
      //   PARENT#level=-N{expr}   N levels UP from this row (relative)
      //   PARENT#level=N{expr}    the ancestor AT absolute level N (root = 1)
      // Returns empty when the walk runs past the root, so a formula that asks
      // for a grandparent on a top-level row yields blank instead of throwing.
      if (node.name === 'PARENT') {
        const lvlRaw = node.scope && node.scope.extra ? node.scope.extra.level : undefined;
        const chain = [];
        for (let a = item.__parent; a; a = a.__parent) chain.push(a); // nearest → root
        let target;
        if (lvlRaw === undefined || lvlRaw === true) target = chain[0];
        else {
          const lvl = parseInt(lvlRaw, 10);
          if (Number.isNaN(lvl)) target = chain[0];
          else if (lvl < 0) target = chain[Math.abs(lvl) - 1];
          else target = chain.length ? chain[chain.length - lvl] : undefined; // absolute: root is level 1
        }
        return target ? evalNode(node.body, target, vars) : undefined;
      }
      // JOIN's default scope is the ancestor path (Structure: "by default it
      // joins all parent string values from root to the self value"); every
      // other aggregate defaults to the row's subtree.
      const scope = node.scope || {};
      const type = scope.type || (node.name === 'JOIN' ? 'ancestors' : DEFAULT_SCOPE);
      const items = scopeItems(item, type, scope.extra);
      const values = items.map((it) => evalNode(node.body, it, vars));
      return applyAggregate(node.name, values, scope);
    }
    case 'call': return evalCall(node, item, vars);
    default: throw new ExprError(`Unknown node "${node.kind}"`, 0);
  }
}

// Call a function value with already-evaluated arguments. Named parameters
// bind by position; an IMPLICIT function (an argument expression written with
// `$`) sees `$` = the first argument and `$1`, `$2`, … = each argument.
function invoke(fn, args, item) {
  const scope = Object.create(fn.closure || null);
  if (fn.implicit) {
    scope.$ = args[0];
    args.forEach((a, i) => { scope[`$${i + 1}`] = a; });
  } else {
    fn.params.forEach((p, i) => { scope[VARKEY(p)] = args[i]; });
  }
  return evalNode(fn.body, item, scope);
}

// The functions that take a function argument (Structure's `$` calls). Their
// callable argument is resolved WITHOUT evaluating it: an explicit lambda, a
// user function passed by name (`MAP(list, square)`), or — the common form —
// any other expression, which becomes an implicit function over `$`.
export const HIGHER_ORDER = new Set(['FILTER', 'MAP', 'REDUCE', 'ALL', 'ANY', 'NONE', 'SORT_BY', 'UMAX_BY', 'UMIN_BY', 'GROUP']);
function toCallable(argNode, item, vars) {
  if (!argNode) return null;
  if (argNode.kind === 'lambda') return evalNode(argNode, item, vars);
  if (argNode.kind === 'field' && vars) {
    const v = vars[VARKEY(argNode.name)];
    if (v && v.__fn) return v;
  }
  return { __fn: true, implicit: true, body: argNode, closure: vars };
}
const asList = (v) => (Array.isArray(v) ? v : (v == null ? [] : [v]));
function evalHigherOrder(name, node, item, vars) {
  const list = asList(evalNode(node.args[0], item, vars));
  const fn = toCallable(node.args[1], item, vars);
  if (!fn) throw new ExprError(`${name} needs a function argument`, 0);
  const call = (...args) => invoke(fn, args, item);
  switch (name) {
    case 'FILTER': return list.filter((x, i) => isTruthy(call(x, i)));
    case 'MAP': return list.map((x, i) => call(x, i));
    case 'ALL': return list.every((x, i) => isTruthy(call(x, i)));
    case 'ANY': return list.some((x, i) => isTruthy(call(x, i)));
    case 'NONE': return !list.some((x, i) => isTruthy(call(x, i)));
    // REDUCE(list, (acc, x) -> …, [initial]) — with `$`: $1 is the running
    // value, $2 the element. Without an initial value the first element seeds
    // it; an empty list reduces to the initial value (or empty).
    case 'REDUCE': {
      const hasInit = node.args.length > 2;
      let acc = hasInit ? evalNode(node.args[2], item, vars) : undefined;
      let i = 0;
      if (!hasInit) { if (!list.length) return undefined; acc = list[0]; i = 1; }
      for (; i < list.length; i++) acc = call(acc, list[i]);
      return acc;
    }
    case 'SORT_BY': {
      const keyed = list.map((x, i) => ({ x, k: call(x, i), i }));
      keyed.sort((a, b) => ucmp(a.k, b.k) || a.i - b.i);
      return keyed.map((e) => e.x);
    }
    case 'UMAX_BY':
    case 'UMIN_BY': {
      let best, bestKey, found = false;
      list.forEach((x, i) => {
        const k = call(x, i);
        if (k == null || k === '') return;
        if (!found || (name === 'UMAX_BY' ? ucmp(k, bestKey) > 0 : ucmp(k, bestKey) < 0)) { best = x; bestKey = k; found = true; }
      });
      return found ? best : undefined;
    }
    // GROUP(list, f) — buckets by the function's value, first-seen order.
    // Each bucket is { key, values }, so `.key` / `.values` read it.
    case 'GROUP': {
      const buckets = new Map();
      list.forEach((x, i) => {
        const k = call(x, i);
        const id = typeof k === 'object' && k !== null ? JSON.stringify(k) : `${typeof k}:${k}`;
        if (!buckets.has(id)) buckets.set(id, { key: k, values: [] });
        buckets.get(id).values.push(x);
      });
      return [...buckets.values()];
    }
    default: throw new ExprError(`Unknown function "${name}"`, 0);
  }
}

function evalCall(node, item, vars) {
  // User-defined functions (WITH f(args) = …) shadow builtins. Arguments are
  // evaluated in the caller's scope; the body runs in the function's closure
  // with parameters bound.
  if (vars) {
    const uf = vars[VARKEY(node.name)];
    if (uf && uf.__fn) return invoke(uf, node.args.map((a) => evalNode(a, item, vars)), item);
  }

  const name = FN_ALIASES[node.name] || node.name;
  const ev = (n) => evalNode(n, item, vars);

  if (HIGHER_ORDER.has(name)) return evalHigherOrder(name, node, item, vars);

  if (LAZY.has(name)) {
    const A = node.args;
    switch (name) {
      case 'IF': {
        // IF(c1, r1, c2, r2, ..., [default]) — first truthy condition wins.
        let i = 0;
        for (; i + 1 < A.length; i += 2) { if (isTruthy(ev(A[i]))) return ev(A[i + 1]); }
        return i < A.length ? ev(A[i]) : undefined; // trailing default
      }
      case 'DEFAULT': { const v = ev(A[0]); return v != null ? v : ev(A[1]); }
      case 'COALESCE': { for (const n of A) { const v = ev(n); if (v != null) return v; } return undefined; }
      // if_error / iferr: fall back on a thrown error OR a non-finite number
      // (divide-by-zero → Infinity/NaN), which Structure treats as an error.
      case 'IFERR': { try { const v = ev(A[0]); return (typeof v === 'number' && !Number.isFinite(v)) ? ev(A[1]) : v; } catch { return ev(A[1]); } }
      case 'ISERR': { try { ev(A[0]); return false; } catch { return true; } }
      // CASE(value, match1, result1, …, [default]). A text match is a Structure
      // pattern — `"/…/"` regex or `*`/`?` wildcards — otherwise plain equality.
      case 'CASE': {
        const v = ev(A[0]);
        let i = 1;
        for (; i + 1 < A.length; i += 2) {
          const m = ev(A[i]);
          const re = typeof m === 'string' ? patternRegex(m) : null;
          if (re ? re.test(str(v)) : binaryEq(v, m)) return ev(A[i + 1]);
        }
        return i < A.length ? ev(A[i]) : undefined;
      }
      case 'CHOOSE': { const idx = num(ev(A[0])); return (idx >= 1 && idx < A.length) ? ev(A[idx]) : undefined; }
      default: break;
    }
  }

  const fn = BUILTINS[name];
  if (!fn) throw new ExprError(`Unknown function "${name}"`, 0);
  return fn(node.args.map(ev), { item, vars });
}

function binaryEq(l, r) {
  if (typeof l === 'number' || typeof r === 'number') return num(l) === num(r);
  return str(l).toLowerCase() === str(r).toLowerCase();
}

// Public: evaluate an AST (or source) against a root item.
// PARENT{…} navigation needs an upward link, but callers build the tree
// downward (children arrays). Link parents once per interpret() call, as a
// non-enumerable property so nothing that serialises or diffs an item tree
// sees it (and so re-linking a shared cached tree is idempotent).
function linkParents(root) {
  const stack = [root];
  while (stack.length) {
    const it = stack.pop();
    for (const c of it.children || []) {
      if (c.__parent !== it) Object.defineProperty(c, '__parent', { value: it, enumerable: false, configurable: true, writable: true });
      stack.push(c);
    }
  }
}

// `vars` seeds the ROOT scope — used for insight-level variables (Structure's
// per-formula Variables panel). WITH still layers on top via Object.create, so a
// local binding shadows a variable of the same name, exactly as in Structure.
export function interpret(astOrSource, rootItem, vars = null) {
  const ast = typeof astOrSource === 'string' ? parse(astOrSource) : astOrSource;
  const root = rootItem || { fields: {}, children: [] };
  linkParents(root);
  return evalNode(ast, root, vars);
}
